<template>
  <form @submit.prevent="submitForm">
    <div class="product__size">
      <SizePickerSuit
        v-if="sizePickerType === SizePickerTypes.SizePickerSuit"
        v-bind="sizePickerSuitProps"
      />
      <SizePickerPants
        v-if="sizePickerType === SizePickerTypes.SizePickerPants"
        v-bind="sizePickerPantsProps"
      />
      <SizePickerRegular
        v-if="sizePickerType === SizePickerTypes.SizePickerRegular"
        v-bind="sizePickerRegularProps"
      />
    </div>
    <div v-if="props.isProductInStock" class="product__add">
      <input type="hidden" value="1" :name='$t("skuParameterCode")' />
      <button
        class="btn btn--profile btn--inset u-display-block btn--add-to-cart product__add-to-cart-btn"
        type="submit"
      >
        {{ $t("addToBasket") }}
      </button>
    </div>

    <!-- Disabled Add to cart button for out-of-stock case -->
    <div v-else class="product__add">
      <button
        class="btn btn--add-to-cart u-display-block product__add-to-cart-btn"
        type="submit"
        disabled
      >
        {{ $t("addToBasket") }}
      </button>
    </div>

    <!--Product availability information-->
    <!-- Show availability only if shopId is not Jean Paul and showAvailability prop is true-->
    <div v-if="props.shopId != 'JeanPaul' && props.showAvailability" class="product__availability">
      <div v-if="isProductInStock" class="product__availability__text">
        <!-- Store inventory button -->
        <Icon :type="IconTypes.AvailableCheck" :size="IconSizes.Micro" />
        <p>
          {{ $t("availableOnline") }}
        </p>
      </div>
      <!-- Sold out message only if product available in physical store -->
      <div v-else class="product__availability__text">
        <Icon :type="IconTypes.SoldoutX" :size="IconSizes.Micro" />
        <p>
          {{ $t("soldoutOnline") }}
        </p>
      </div>

      <div v-if="productAvailableInStore" class="product__availability-link product__availability__instore-text">
        <!-- Store inventory button -->
        <Icon :type="IconTypes.AvailableCheck" :size="IconSizes.Micro" />
        <button
          class="btn--link  _js-vue-component-toggler"
          data-togglewhat="storeInventory"
        >
        <p>
          {{ $t("availableInStore") }}
        </p>
      </button>
      </div>
      <!-- Sold out message only if product available in physical store -->
      <div v-else class="product__availability__text">
        <Icon :type="IconTypes.SoldoutX" :size="IconSizes.Micro" />
        <p>
          {{ $t("soldoutInStores") }}
        </p>
      </div>
    </div>
  </form>
  <CartStatus
    v-if="props.checkoutLink && props.displayName && props.cartStatusImage"
    :display-name="props.displayName"
    :item-color="props.itemColor"
    :item-meta-color="props.itemMetaColor"
    :cart-status-image="props.cartStatusImage"
    :checkout-link="props.checkoutLink"
    :esalesKey="props.esalesKey"
    :categoryName="props.categoryName"
  />
</template>

<script lang="ts" setup>
import {computed, onBeforeMount, onMounted, onUnmounted, ref, watch, withDefaults} from "vue";
import SizePickerSuit from "./sizePickerSuit.vue";
import SizePickerPants from "./sizePickerPants.vue";
import SizePickerRegular from "./sizePickerRegular.vue";
import {validateSizeSelection} from "../../mixins/productSize.mixin";
import {useProductPageStore} from "../../stores/productPageStore";
import {vueComponentToggler} from "../../../../global/modules/toggler_wp";
import {vueInventory} from "../../../../global/modules/inventory_wp";
import Icon from "../../components/icon/icon.vue";
import {IconSizes, IconTypes} from "../../components/icon/icon.model";
import CartStatus from "../../views/cartStatus/cartStatus.vue";
import ShoppingCartService from "../../services/shoppingcart.service";
import { VoyadoEngageWebTrackingService } from "../../services/voyadoEngageWebTracking.service";
import { AddToCartFormProps } from "./addToCartForm.models";

const productPageStore = useProductPageStore();

const props = withDefaults(defineProps<AddToCartFormProps>(), {
  isSizeGuide: false,
  showInventoryNotification: false,
  showAvailability: false,
  isQuickAdd: false,
});

const formSubmitted = ref(false);

const SizePickerTypes = {
  SizePickerSuit: "suit",
  SizePickerPants: "pants",
  SizePickerRegular: "regular",
} as const;

const sizePickerType = computed(() => {
  try {
    if (props.matrixSizes.length > 0) {
      const firstSize = props.matrixSizes[0][0].name ?? "";
      return isSuitSize(firstSize)
        ? SizePickerTypes.SizePickerSuit
        : SizePickerTypes.SizePickerPants;
    }

    return SizePickerTypes.SizePickerRegular;
  } catch (error) {
    console.error("Error parsing matrixSizes:", error);
    return SizePickerTypes.SizePickerRegular;
  }
});

// Function to determine if it's a suit size
const isSuitSize = (sizeName: string): boolean => {
  return /^[BC]\d+$/.test(sizeName); // Starts with B or C followed by digits
};

const fewItemsLeftThreshold = computed(() => {
  return parseInt('$t("fewItemsLeftThreshold")');;
});

const sizePickerRegularProps = computed(() => {
  return {
    isSizeGuide: props.isSizeGuide,
    regularSizes: props.regularSizes,
    showInventoryNotification: props.showInventoryNotification,
  };
});

const sizePickerPantsProps = computed(() => {
  return {
    isSizeGuide: props.isSizeGuide,
    matrixSizes: props.matrixSizes,
    showInventoryNotification: props.showInventoryNotification,
  };
});

const sizePickerSuitProps = computed(() => {
  return {
    isSizeGuide: props.isSizeGuide,
    matrixSizes: props.matrixSizes,
    showInventoryNotification: props.showInventoryNotification,
  };
});

const validateSelection = () => {
  return validateSizeSelection(sizePickerType.value, productPageStore);
};

async function checkStockStatus(
  count: string,
  treshold: number = 0,
): Promise<void> {
  const inventoryIsLow = parseInt(count) <= treshold;

  if (inventoryIsLow) {
    productPageStore.inventoryIsLow = true;
  } else {
    productPageStore.inventoryIsLow = false;
  }

  productPageStore.setCheckInventoryStatus(false);
}

const submitForm = async () => {
  if (!validateSelection()) {
    return;
  }

  await ShoppingCartService.apiAddProductToCart(
    productPageStore.skuCode, 
    props.isQuickAdd, props.itemListId || "", 
    props.itemListName || "", 
    productPageStore.apptusTicket
  );
  
  if (!props.isQuickAdd) {
    productPageStore.setCartStatusIsOpen(true);
  }

  formSubmitted.value = true;

  if (props.addToCartCallback) {
    props.addToCartCallback();
  }
};

onBeforeMount(() => {
  if (props.isQuickAdd) {productPageStore.resetState();}
});

onUnmounted(() => {
  productPageStore.resetState();

  // Scroll to the last added lineItem only if the form is submitted
  if (formSubmitted.value) {
    const checkoutLineItems = document.querySelectorAll(".checkout-line-item");

    if (checkoutLineItems.length > 0) {
      const lastCheckoutLineItem = checkoutLineItems[checkoutLineItems.length - 1];

      lastCheckoutLineItem.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
});

onMounted(() => {
  vueComponentToggler();
  vueInventory();

  productPageStore.setItemColor(props.itemColor ?? "");
  productPageStore.setItemMetaColor(props.itemMetaColor ?? "");
  productPageStore.setDisplayName(props.displayName ?? "");
  productPageStore.setCartStatusIsOpen(false);
  VoyadoEngageWebTrackingService.trackProductView({
    itemId: props.esalesKey,
    categoryName: decodeURIComponent(props.categoryName.replace(/\+/g, " "))
  })
});

watch(
  () => productPageStore.checkInventoryStatus,
  (newValue) => {
    if (newValue === true) {
      // Trigger the API call with the inventory count value
      checkStockStatus(
        productPageStore.inventoryCount,
        fewItemsLeftThreshold.value,
      );
    }
  },
);

</script>
